import React, { useEffect, useState } from 'react';
import { XIcon, DownloadIcon, PrinterIcon, MaximizeIcon, MinimizeIcon, ChevronLeftIcon, ChevronRightIcon, ZoomInIcon, ZoomOutIcon } from 'lucide-react';
interface PDFPreviewModalProps {
  document: {
    id: string;
    name: string;
    previewUrl?: string;
  };
  onClose: () => void;
}
export const PDFPreviewModal: React.FC<PDFPreviewModalProps> = ({
  document,
  onClose
}) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [zoomLevel, setZoomLevel] = useState(100);
  const totalPages = 3; // Mock value
  // Handle keyboard events (ESC to close)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);
  // Simulate document loading
  useEffect(() => {
    setIsLoading(true);
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1500);
    return () => clearTimeout(timer);
  }, [document]);
  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };
  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      setIsLoading(true);
      setTimeout(() => {
        setIsLoading(false);
      }, 500);
    }
  };
  const handleZoom = (action: 'in' | 'out' | 'reset') => {
    if (action === 'in' && zoomLevel < 200) {
      setZoomLevel(zoomLevel + 25);
    } else if (action === 'out' && zoomLevel > 50) {
      setZoomLevel(zoomLevel - 25);
    } else if (action === 'reset') {
      setZoomLevel(100);
    }
  };
  // Handle click outside to close
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };
  return <div className={`fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4 ${isFullscreen ? '' : 'backdrop-blur-sm'} transition-all duration-300`} onClick={handleBackdropClick} data-id="element-582">
      <div className={`bg-white rounded-lg shadow-xl overflow-hidden flex flex-col ${isFullscreen ? 'w-full h-full rounded-none' : 'w-full max-w-4xl h-[90vh]'} transition-all duration-300`} onClick={e => e.stopPropagation()} data-id="element-583">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center bg-white" data-id="element-584">
          <h2 className="text-lg font-semibold text-gray-800 flex items-center" data-id="element-585">
            <FileIcon className="h-5 w-5 text-blue-600 mr-2" data-id="element-586" />
            {document.name}
          </h2>
          <div className="flex items-center space-x-2" data-id="element-587">
            <button onClick={toggleFullscreen} className="p-1.5 text-gray-500 hover:text-gray-700 rounded-md hover:bg-gray-100 transition-colors" aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'} data-id="element-588">
              {isFullscreen ? <MinimizeIcon className="h-5 w-5" data-id="element-589" /> : <MaximizeIcon className="h-5 w-5" data-id="element-590" />}
            </button>
            <button onClick={onClose} className="p-1.5 text-gray-500 hover:text-gray-700 rounded-md hover:bg-gray-100 transition-colors" aria-label="Close" data-id="element-591">
              <XIcon className="h-5 w-5" data-id="element-592" />
            </button>
          </div>
        </div>
        {/* Toolbar */}
        <div className="px-6 py-2 border-b border-gray-200 bg-gray-50 flex justify-between items-center" data-id="element-593">
          <div className="flex items-center space-x-2" data-id="element-594">
            <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1 || isLoading} className={`p-1.5 rounded-md ${currentPage === 1 || isLoading ? 'text-gray-300 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-200 transition-colors'}`} aria-label="Previous page" data-id="element-595">
              <ChevronLeftIcon className="h-5 w-5" data-id="element-596" />
            </button>
            <span className="text-sm text-gray-600" data-id="element-597">
              Page {currentPage} of {totalPages}
            </span>
            <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages || isLoading} className={`p-1.5 rounded-md ${currentPage === totalPages || isLoading ? 'text-gray-300 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-200 transition-colors'}`} aria-label="Next page" data-id="element-598">
              <ChevronRightIcon className="h-5 w-5" data-id="element-599" />
            </button>
          </div>
          <div className="flex items-center space-x-2" data-id="element-600">
            <button onClick={() => handleZoom('out')} disabled={zoomLevel <= 50 || isLoading} className={`p-1.5 rounded-md ${zoomLevel <= 50 || isLoading ? 'text-gray-300 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-200 transition-colors'}`} aria-label="Zoom out" data-id="element-601">
              <ZoomOutIcon className="h-5 w-5" data-id="element-602" />
            </button>
            <span className="text-sm text-gray-600 min-w-[48px] text-center" data-id="element-603">
              {zoomLevel}%
            </span>
            <button onClick={() => handleZoom('in')} disabled={zoomLevel >= 200 || isLoading} className={`p-1.5 rounded-md ${zoomLevel >= 200 || isLoading ? 'text-gray-300 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-200 transition-colors'}`} aria-label="Zoom in" data-id="element-604">
              <ZoomInIcon className="h-5 w-5" data-id="element-605" />
            </button>
            <button onClick={() => handleZoom('reset')} disabled={zoomLevel === 100 || isLoading} className={`p-1.5 text-xs rounded-md ${zoomLevel === 100 || isLoading ? 'text-gray-300 cursor-not-allowed' : 'text-blue-600 hover:bg-blue-50 transition-colors'}`} data-id="element-606">
              Reset
            </button>
          </div>
        </div>
        {/* Document Content */}
        <div className="flex-1 overflow-auto bg-gray-100 flex items-center justify-center p-4" data-id="element-607">
          {isLoading ? <div className="flex flex-col items-center justify-center" data-id="element-608">
              <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4" data-id="element-609"></div>
              <p className="text-gray-600 animate-pulse" data-id="element-610">
                Loading document preview...
              </p>
            </div> : <div className="bg-white shadow-lg mx-auto transition-all duration-300 transform" style={{
          width: `${zoomLevel}%`,
          maxWidth: '100%',
          height: 'auto',
          minHeight: '80%',
          maxHeight: zoomLevel > 100 ? 'none' : '90%'
        }} data-id="element-611">
              {/* PDF Content */}
              <img src={document.previewUrl || 'https://uploadthingy.s3.us-west-1.amazonaws.com/sTVL3NFLjSiFiLynqjbUJ9/image.png'} alt={`${document.name} preview`} className="w-full h-full object-contain" data-id="element-612" />
            </div>}
        </div>
        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-white flex justify-between items-center" data-id="element-613">
          <div className="text-sm text-gray-500" data-id="element-614">
            Document ID: {document.id}
          </div>
          <div className="flex items-center space-x-3" data-id="element-615">
            <button className="flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 transition-colors" disabled={isLoading} data-id="element-616">
              <PrinterIcon className="h-4 w-4 mr-1.5" data-id="element-617" />
              Print
            </button>
            <button className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 transition-colors" disabled={isLoading} data-id="element-618">
              <DownloadIcon className="h-4 w-4 mr-1.5" data-id="element-619" />
              Download
            </button>
          </div>
        </div>
      </div>
    </div>;
};
// Helper component for file icon
const FileIcon = ({
  className
}: {
  className?: string;
}) => <svg className={className} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-620">
    <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-621" />
    <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-622" />
    <path d="M16 13H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-623" />
    <path d="M16 17H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-624" />
    <path d="M10 9H9H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-625" />
  </svg>;